import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Mail, MessageCircle, ChevronDown, ChevronRight, Megaphone, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NewEmailCampaignDialog } from './NewEmailCampaignDialog';

const STATUS = {
  sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  whatsapp: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sms: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-gray-50 text-gray-500 border-gray-200',
  failed: 'bg-red-50 text-red-600 border-red-200',
  unknown: 'bg-gray-50 text-gray-400 border-gray-200',
};
const STATUS_LABEL = { sent: 'Sent', whatsapp: 'Texted via WhatsApp', sms: 'Texted via SMS', pending: 'Not texted', failed: 'Failed', unknown: 'No detail' };

function CampaignRow({ c }) {
  const [open, setOpen] = useState(false);
  const Icon = c.channel === 'email' ? Mail : MessageCircle;
  return (
    <div data-testid={`campaign-${c.id}`} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <button data-testid={`campaign-toggle-${c.id}`} onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors">
        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${c.channel === 'email' ? 'bg-blue-50 text-[#0073ea]' : 'bg-emerald-50 text-[#25D366]'}`}><Icon className="h-4 w-4" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{c.subject || c.message}</p>
          <p className="text-[11px] text-gray-500">{new Date(c.created_at).toLocaleString()}{c.lead_address && <> · {c.lead_address}</>}</p>
        </div>
        <span data-testid={`campaign-sent-${c.id}`} className="text-sm font-semibold text-gray-900 shrink-0">{c.sent_count}/{c.total} <span className="text-xs font-normal text-gray-400">{c.channel === 'email' ? 'sent' : 'texted'}</span></span>
      </button>
      {open && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3 bg-gray-50/50">
          {c.lead_id && <Link to={`/leads/${c.lead_id}`} className="text-xs text-[#0073ea] hover:underline">Open property: {c.lead_address || 'View lead'}</Link>}
          <p className="text-xs text-gray-600 whitespace-pre-wrap bg-white border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">{c.message}</p>
          <div className="space-y-1">
            {c.recipients.map(r => (
              <div key={r.buyer_id} data-testid={`campaign-recipient-${c.id}-${r.buyer_id}`} className="flex items-center gap-3 text-sm">
                <Link to={`/buyers/${r.buyer_id}`} className="flex-1 min-w-0 truncate text-gray-900 hover:text-[#0073ea]">{r.name} <span className="text-gray-400 text-xs">{r.contact}</span></Link>
                {r.error && <span className="text-[11px] text-red-500">{r.error}</span>}
                <Badge variant="outline" className={`text-[10px] ${STATUS[r.status] || STATUS.unknown}`}>{STATUS_LABEL[r.status] || r.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CampaignHistory() {
  const [items, setItems] = useState(null);
  const [channel, setChannel] = useState('');
  const [creating, setCreating] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    API.get('/buyer-intel/campaigns', { params: channel ? { channel } : {} }).then(r => setItems(r.data)).catch(() => setItems([]));
  }, [channel, reloadKey]);

  return (
    <div className="space-y-3" data-testid="campaign-history">
      <div className="flex items-center gap-2">
        <Button size="sm" data-testid="new-email-campaign-btn" onClick={() => setCreating(true)} className="order-last ml-auto bg-[#0073ea] hover:bg-[#0060c2] text-white"><Plus className="h-4 w-4 mr-1" /> New Email Campaign</Button>
        {[['', 'All'], ['email', 'Email'], ['text', 'Text']].map(([k, label]) => (
          <button key={label} data-testid={`campaign-filter-${label.toLowerCase()}`} onClick={() => setChannel(k)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${channel === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}>{label}</button>
        ))}
      </div>
      {items === null ? <div className="h-40 rounded-xl bg-gray-100 animate-pulse" /> : items.length === 0 ? (
        <div className="text-center py-14" data-testid="no-campaigns"><Megaphone className="h-10 w-10 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">No campaigns yet. Select buyers and send an email or text.</p></div>
      ) : items.map(c => <CampaignRow key={c.id} c={c} />)}
      <NewEmailCampaignDialog open={creating} onOpenChange={setCreating} onSent={() => setReloadKey(k => k + 1)} />
    </div>
  );
}
